import { Link } from 'react-router-dom';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/" className="text-sm text-blue-600 hover:text-blue-800 mb-8 inline-block">&larr; Back to Jubly</Link>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-10">Last updated: March 25, 2026</p>

        <div className="prose prose-gray max-w-none text-[15px] leading-relaxed">
          <h2>1. Introduction</h2>
          <p>
            Jubly ("we," "our," or "us") is operated by Hapi.vc, LLC, located at 110 Westwood Rd, Goshen, Indiana 46526, United States. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our communication platform including email, calendar, voice calling, SMS messaging, and video meeting services (collectively, the "Service").
          </p>
          <p>
            By using Jubly, you agree to the collection and use of information in accordance with this policy. If you do not agree, please do not use the Service.
          </p>

          <h2>2. Information We Collect</h2>
          <h3>Account Information</h3>
          <p>When you create an account, we collect your name, email address, password, and recovery email address.</p>

          <h3>Communication Data</h3>
          <p>We store emails, calendar events, SMS messages, call logs, voicemail recordings and transcriptions, and meeting recordings that you create or receive through the Service. This data is necessary to provide the Service.</p>

          <h3>Contact Information</h3>
          <p>We store contacts you create, including names, phone numbers, and email addresses.</p>

          <h3>Phone Numbers</h3>
          <p>If you use our voice or SMS services, we provision phone numbers on your behalf and store call/message metadata including timestamps, duration, and phone numbers involved.</p>

          <h3>Usage Data</h3>
          <p>We automatically collect information about how you interact with the Service, including IP addresses, browser type, pages visited, and feature usage.</p>

          <h3>Device Information</h3>
          <p>We collect information about the device you use to access the Service, including device type, operating system, and browser.</p>

          <h2>3. How We Use Your Information</h2>
          <ul>
            <li>To provide, maintain, and improve the Service</li>
            <li>To send and receive emails, SMS messages, and phone calls on your behalf</li>
            <li>To provide calendar and scheduling functionality</li>
            <li>To provide video meeting and collaboration features</li>
            <li>To send you account-related notifications (e.g., verification codes, security alerts)</li>
            <li>To provide customer support</li>
            <li>To detect, prevent, and address fraud, abuse, and technical issues</li>
            <li>To comply with legal obligations</li>
          </ul>

          <h2>4. SMS/MMS Messaging</h2>
          <p>
            When you use Jubly's SMS/MMS messaging features:
          </p>
          <ul>
            <li>Messages are sent from phone numbers provisioned through our telecommunications provider (Telnyx)</li>
            <li>Message content, sender/recipient numbers, and timestamps are stored to provide the Service</li>
            <li>We do not sell or share your SMS data with third parties for marketing purposes</li>
            <li>Standard messaging rates from your carrier may apply for messages you receive</li>
            <li>You can opt out of receiving messages at any time by replying STOP</li>
          </ul>

          <h2>5. Voice Calling</h2>
          <p>
            When you use Jubly's voice calling features:
          </p>
          <ul>
            <li>Call metadata (caller/callee numbers, duration, timestamps) is stored</li>
            <li>Voicemail recordings are stored and may be transcribed using AI</li>
            <li>Calls may be recorded if you enable recording; participants are notified</li>
            <li>WebRTC is used for browser-based calling with end-to-end encryption</li>
          </ul>

          <h2>6. Data Storage and Security</h2>
          <p>
            Your data is stored on secure servers hosted by Railway (application hosting) and managed PostgreSQL databases. Email attachments and recordings are stored in S3-compatible object storage. We use encryption in transit (TLS/HTTPS) and encryption at rest for sensitive data including passwords (bcrypt) and API keys (AES-256-GCM).
          </p>

          <h2>7. Third-Party Services</h2>
          <p>We use the following third-party services to provide the Service:</p>
          <ul>
            <li><strong>Telnyx</strong> — Voice calling, SMS messaging, and phone number provisioning</li>
            <li><strong>Resend</strong> — Outbound email delivery</li>
            <li><strong>Cloudflare</strong> — DNS, email routing, CDN, and security</li>
            <li><strong>Railway</strong> — Application hosting and database</li>
            <li><strong>Daily.co</strong> — Video conferencing</li>
          </ul>
          <p>Each third-party service has its own privacy policy governing the data they process on our behalf.</p>

          <h2>8. Data Retention</h2>
          <p>
            We retain your data for as long as your account is active or as needed to provide the Service. Deleted emails are permanently removed after 30 days in Trash. You may request deletion of your account and associated data at any time by contacting us.
          </p>

          <h2>9. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access your personal data</li>
            <li>Correct inaccurate data</li>
            <li>Delete your account and data</li>
            <li>Export your data</li>
            <li>Opt out of non-essential communications</li>
          </ul>

          <h2>10. Children's Privacy</h2>
          <p>The Service is not intended for children under 13. We do not knowingly collect information from children under 13.</p>

          <h2>11. Changes to This Policy</h2>
          <p>We may update this policy from time to time. We will notify you of material changes by posting the updated policy on this page and updating the "Last updated" date.</p>

          <h2>12. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy, contact us at:
          </p>
          <p>
            Hapi.vc, LLC<br />
            110 Westwood Rd<br />
            Goshen, Indiana 46526<br />
            Email: <a href="mailto:support@jubly.net">support@jubly.net</a><br />
            Phone: +1 (574) 216-2626
          </p>
        </div>
      </div>
    </div>
  );
}
